import React from 'react';
import type { CtaData, Page, Component } from '../ComponentEditModal.types';
import { ButtonsEditor } from '../shared/ButtonsEditor';
import type { ButtonData } from '../shared/ButtonsEditor';
import { BackgroundImageEditor } from '../shared/BackgroundImageEditor';
import { ColorCustomizationPanel } from '../shared/ColorInput';
import { getColorFieldsForComponent } from '../shared/colorFieldUtils';
import { useCustomColors } from '../../../contexts/CustomColorsContext';

interface CtaEditorProps {
  formData: CtaData;
  setFormData: (data: CtaData) => void;
  pages: Page[];
  isLoadingPages: boolean;
  onOpenMediaUpload: (mediaType: 'image', assetType: 'background') => void;
  currentPageComponents?: Component[];
}

export const CtaEditor: React.FC<CtaEditorProps> = ({
  formData,
  setFormData,
  pages,
  isLoadingPages,
  onOpenMediaUpload,
  currentPageComponents,
}) => {
  const data = formData;
  const themeColors = useCustomColors();

  const handleButtonsChange = (buttons: ButtonData[]) => {
    setFormData({ ...data, buttons: buttons as CtaData['buttons'] });
  };

  const updateCustomColor = (key: string, value: string | null) => {
    setFormData({
      ...data,
      customColors: { ...(data as any).customColors, [key]: value },
    } as any);
  };

  // Get theme-aware color defaults
  const colorFields = getColorFieldsForComponent('cta', themeColors);

  return (
    <div className="space-y-6">
      {/* Title */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
        <textarea
          value={data.title || ''}
          onChange={e => setFormData({ ...data, title: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
          rows={2}
          placeholder="Enter call-to-action title"
          required
        />
      </div>

      {/* Subtitle */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Subtitle (optional)</label>
        <textarea
          value={data.subtitle || ''}
          onChange={e => setFormData({ ...data, subtitle: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
          rows={2}
          placeholder="Enter subtitle or description"
        />
      </div>

      {/* Alignment */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Alignment</label>
        <select
          value={data.alignment || 'center'}
          onChange={e =>
            setFormData({ ...data, alignment: e.target.value as 'left' | 'center' | 'right' })
          }
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
        >
          <option value="left">Left</option>
          <option value="center">Center</option>
          <option value="right">Right</option>
        </select>
      </div>

      {/* Buttons */}
      <ButtonsEditor
        buttons={(data.buttons || []) as ButtonData[]}
        onChange={handleButtonsChange}
        pages={pages}
        isLoadingPages={isLoadingPages}
        currentPageComponents={currentPageComponents}
      />

      {/* Background Image */}
      <div className="border-t pt-6">
        <h3 className="text-md font-medium text-gray-900 mb-4">Background Image</h3>
        <BackgroundImageEditor
          backgroundImage={(data as any).backgroundImage}
          onChange={backgroundImage => setFormData({ ...data, backgroundImage } as any)}
          onOpenMediaUpload={() => onOpenMediaUpload('image', 'background')}
        />
      </div>

      {/* Custom Colors */}
      <ColorCustomizationPanel
        title="Color Customization"
        description="Customize CTA section colors. Leave empty to use defaults."
        customColors={(data as any).customColors as Record<string, string | null | undefined>}
        colorFields={colorFields}
        onUpdateColor={updateCustomColor}
      />
    </div>
  );
};
